import { Injectable } from '@angular/core';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import {
	HardwareScanTestResult,
	HardwareScanOverallResult,
} from 'src/app/modules/hardware-scan/enums/hardware-scan.enum';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';
import { HardwareScanResultService } from 'src/app/modules/hardware-scan/services/hardware-scan-result.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
	Group,
	HardwareScanItemDetails,
	HardwareScanPluginGetPreviousResultResponse,
} from '../models/hardware-scan.interface';
import { CommonService } from 'src/app/services/common/common.service';

@Injectable({
	providedIn: 'root',
})
export class PreviousResultService {
	private hardwareScanBridge: any;
	private viewResultItems: any;

	private hasLastResults = false;
	private previousResults: HardwareScanPluginGetPreviousResultResponse = {};
	private previousItemsWidget = {};
	private previousResultsResponse: any = undefined;

	public shouldUpdatePreviousResult = false;

	constructor(
		shellService: VantageShellService,
		private commonService: CommonService,
		private hardwareScanResultService: HardwareScanResultService,
		private loggerService: LoggerService
	) {
		this.hardwareScanBridge = shellService.getHardwareScan();
	}

	public getLastPreviousResultCompletionInfo() {
		const item: any = this.getPreviousResultsWidget();
		return {
			date: item?.date,
			result: !item?.modules
				? null
				: HardwareScanTestResult[
						this.hardwareScanResultService.consolidateResults(
							item.modules.map((module) => module.resultModule)
						)
				  ],
		};
	}

	private buildPreviousResultsWidget(previousResults: any) {
		const previousItems: any = {};

		try {
			previousItems.date = previousResults.date;
			previousItems.modules = [];
			for (const item of previousResults.items) {
				const module: any = {};
				module.name = item.module;
				module.subname = item.name;
				module.resultModule = this.hardwareScanResultService.consolidateResults(
					item.listTest.map((itemTest) => itemTest.statusTest)
				);

				previousItems.modules.push(module);
			}
		} catch (ex) {
			this.loggerService.exception(
				'[PreviousResultService] buildPreviousResultsWidget - Failed to build previous results widget',
				ex
			);
		}

		this.previousItemsWidget = previousItems;
	}

	public buildPreviousResults(response: any) {
		const previousResults: any = {};
		let moduleId = 0;
		this.hasLastResults = false;

		if (response?.hasPreviousResults) {
			this.hasLastResults = response.hasPreviousResults;
			previousResults.finalResultCode = response.scanSummary.finalResultCode;
			previousResults.resultTestsTitle = HardwareScanTestResult.Pass;

			previousResults.date = new Date(response.scanSummary.ScanDate);

			previousResults.information = response.scanSummary.finalResultCodeDescription;
			previousResults.items = [];

			for (const module of response.modulesResults) {
				const groupResult = module.response.groupResults;
				const groupsResultMeta = module.categoryInformation.groupList;

				for (let i = 0; i < module.response.groupResults.length; i++) {
					const item: any = {};
					const groupResultMeta = groupsResultMeta.find(
						(x) => x.id === groupResult[i].id
					);
					const moduleName = groupResult[i].moduleName;

					item.id = moduleId;
					item.module = module.categoryInformation.name;
					item.name = groupResultMeta.name;
					item.resultCode = groupResult[i].resultCode;
					item.information = groupResult[i].resultDescription;
					item.expanded = false;
					item.expandedStatusChangedByUser = false;
					item.detailsExpanded = false;
					item.icon = moduleName;
					item.resultModule = HardwareScanTestResult.Pass;

					// Use this validation prevent cyclical dependency with hardwareScanService
					// [NOTICE] When remove the isDesktopMachine from hardware-scan.service to another service
					// change this line to use the newest function
					const desktopMachine = this.commonService.getLocalStorageValue(
						LocalStorageKey.DesktopMachine
					);
					if (!desktopMachine) {
						if (item.icon === 'pci_express') {
							item.icon += '_laptop';
						}
					}
					item.details = this.buildDetails(groupResultMeta);
					item.listTest = [];
					const test = groupResult[i].testResultList;
					const testMeta = groupResultMeta.testList;

					for (let j = 0; j < groupResult[i].testResultList.length; j++) {
						const testInfo: any = {};
						testInfo.id = test[j].id;
						testInfo.name = testMeta.find((x) => x.id === test[j].id).name;
						testInfo.information = testMeta.find(
							(x) => x.id === test[j].id
						).description;
						testInfo.statusTest = test[j].result;

						if (
							testInfo.statusTest === HardwareScanTestResult.NotStarted ||
							testInfo.statusTest === HardwareScanTestResult.InProgress
						) {
							testInfo.statusTest = HardwareScanOverallResult.Cancelled;
						}
						item.listTest.push(testInfo);
					}
					item.resultModule = this.hardwareScanResultService.consolidateResults(
						test.map((itemTest) => itemTest.result)
					);
					previousResults.items.push(item);
				}

				moduleId++;
			}
			previousResults.resultTestsTitle = this.hardwareScanResultService.consolidateResults(
				previousResults.items.map((item) => item.resultModule)
			);
		}

		// This will clean the previous result widget and previous result when a null response is received
		this.previousResults = previousResults;
		this.buildPreviousResultsWidget(this.previousResults);
	}

	public buildDetails(module: Group): HardwareScanItemDetails[] {
		const result: HardwareScanItemDetails[] = [];
		let index: string = undefined;

		for (const item of module.metaInformation) {
			// Preventing UDI to be displayed on details
			if (item.name !== 'UDI') {
				// If a detail group has an index,
				// a new detail with index is added
				// for display in the Details List
				if (item.index && item.index !== index) {
					index = item.index;

					result.push({
						index: item.index,
						key: 'INDEX',
						value: item.index,
					});
				}

				result.push({
					index: item.index,
					key: item.name,
					value: item.value,
				});
			}
		}

		return result;
	}

	public getLastResults() {
		if (this.hardwareScanBridge) {
			if (this.shouldUpdatePreviousResult) {
				this.updatePreviousResultsResponse();
			}
			return this.previousResultsResponse
				.then((response) => {
					this.buildPreviousResults(response);
					return response;
				})
				.catch((ex) => {
					this.loggerService.exception(
						'[getLastResults] -> buildPreviousResults error',
						ex
					);
				});
		}
		return undefined;
	}

	public getLastFinalResultCode() {
		const item: any = this.getViewResultItems();
		return item.finalResultCode;
	}

	public updateLastFailuredTest(moduleList: any) {
		// Clear Failed Tests before count
		this.hardwareScanResultService.clearFailedTests();

		moduleList.forEach((module) => {
			this.hardwareScanResultService.countFailedTests(module.listTest);
		});
	}

	public updatePreviousResultsResponse() {
		this.previousResultsResponse = this.hardwareScanBridge
			.getPreviousResults()
			.then((response) => {
				this.shouldUpdatePreviousResult = false;
				this.hasLastResults = true;
				return response;
			})
			.catch((ex) => {
				this.loggerService.exception('[updatePreviousResultsResponse]', ex);
			});
	}

	public getPreviousResultsWidget() {
		return this.previousItemsWidget;
	}

	public hasPreviousResults() {
		return this.hasLastResults;
	}

	public getPreviousResults(): HardwareScanPluginGetPreviousResultResponse {
		return this.previousResults;
	}

	public getViewResultItems() {
		return this.viewResultItems;
	}

	public setViewResultItems(items: any) {
		this.viewResultItems = items;
	}

	public async getLastHardwareScanDate(): Promise<any> {
		this.updatePreviousResultsResponse();
		await this.getLastResults();
		const previousResultsWidget: any = this.getPreviousResultsWidget();
		if (previousResultsWidget) {
			return previousResultsWidget.date;
		} else {
			return undefined;
		}
	}
}
